<template>
  <div class="header-nav-dropdown">
    <SButton
      ref="navButtonRef"
      action-type="quaternary"
      :aria-expanded="isOpen"
      size="sm"
      :suffix-icon="isOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
      @click="toggleByClick"
      @keydown.enter.prevent="toggleByKeyboard"
      @keydown.escape="close"
    >
      {{ label }}
    </SButton>

    <Teleport to="body">
      <Transition name="header-nav-dropdown__panel">
        <nav
          v-if="isOpen"
          ref="panelRef"
          class="header-nav-dropdown__panel"
          data-gtm-container="header-menu"
          @click.self="close"
          @keydown.escape="close"
          @keydown.tab="handleTab"
        >
          <div class="header-nav-dropdown__container">
            <slot />
          </div>
        </nav>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, useMediaQuery, useScroll } from "@vueuse/core";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

import { SButton } from "@solvari/common-fe";

defineProps<{
  label: string;
}>();

const isOpen = ref(false);
const isDesktop = useMediaQuery("(min-width: 1000px)");
const { y: scrollY } = useScroll(window);

watch(isDesktop, (desktop) => {
  if (!desktop) {
    isOpen.value = false;
  }
});

watch(scrollY, () => {
  if (isOpen.value) {
    isOpen.value = false;
  }
});

const navButtonRef =
  useTemplateRef<InstanceType<typeof SButton>>("navButtonRef");
const panelRef = useTemplateRef<HTMLElement>("panelRef");

const navButtonEl = computed(
  () => navButtonRef.value?.$el as HTMLElement | undefined,
);

onClickOutside(
  panelRef,
  () => {
    isOpen.value = false;
  },
  {
    ignore: [navButtonEl],
    capture: true,
  },
);

const focusableSelector = "a.s-button, button.s-button";
const openedByKeyboard = ref(false);

const focusableElements = computed(() =>
  panelRef.value?.querySelectorAll(focusableSelector),
);

watch(isOpen, async (open) => {
  if (open && openedByKeyboard.value) {
    await nextTick();
    (focusableElements.value?.[0] as HTMLElement | null)?.focus();
  }
  if (!open) {
    openedByKeyboard.value = false;
  }
});

function toggleByClick() {
  openedByKeyboard.value = false;
  isOpen.value = !isOpen.value;
}

function toggleByKeyboard() {
  openedByKeyboard.value = true;
  isOpen.value = !isOpen.value;
}

function close() {
  isOpen.value = false;
  navButtonEl.value?.focus();
}

function handleTab(event: KeyboardEvent) {
  if (!focusableElements.value?.length) return;

  const firstElement = focusableElements.value[0] as HTMLElement;
  const lastElement = focusableElements.value[
    focusableElements.value.length - 1
  ] as HTMLElement;

  if (
    (event.shiftKey && document.activeElement === firstElement) ||
    (!event.shiftKey && document.activeElement === lastElement)
  ) {
    event.preventDefault();
    close();
  }
}
</script>

<style lang="postcss">
.header-nav-dropdown {
  &__panel {
    @apply absolute left-0 right-0 top-24 z-40 flex justify-center px-3; /* top-24 = 96px = s-header-base (64px) + HeaderContact (32px) */
  }

  &__container {
    @apply w-screen-1200 max-w-full rounded-b-lg bg-white shadow-lg;
  }

  &__panel-enter-active,
  &__panel-leave-active {
    @apply transition-all duration-200 ease-out;
  }

  &__panel-enter-from,
  &__panel-leave-to {
    @apply translate-y-[-8px] opacity-0;
  }

  &__panel-enter-to,
  &__panel-leave-from {
    @apply translate-y-0 opacity-100;
  }
}
</style>
